import type { INoteInitials } from './api/internal-field-contracts';
import { utcDay } from './components/days';

/**
 * A survey's staff notes as the team writes them: one text field kept as a log, newest entry
 * first, entries joined by `//`, each `M/D/YY XX - text` — the day, the analyst's initials and
 * what happened. The convention is typed by hand (Salesforce's `Notes__c`, migrated as is), so
 * the spacing varies and a part that does not read as an entry is kept whole as its text.
 */

export interface INoteEntry {
  /** `YYYY-MM-DD`, or null where the part carries no day that exists. */
  day: Nullable<string>;
  initials: Nullable<string>;
  text: string;
}

/** `//` between entries, never the one in `https://`. */
const BETWEEN = /(?<!:)\/{2,}/;
/** The day, then initials only where a hyphen follows them and they look like initials — up to
 *  three capitals, two small letters, or two capitals and a small one, the form survey-api signs a
 *  colleague with when two capitals are somebody else's ("DFe", SR-494) — so "3/4/25 Called" and
 *  "6/1/25 Sent - …" keep their word. */
const ENTRY = /^(\d{1,2})\/(\d{1,2})\/(\d{4}|\d{2})\b\s*(?:([A-Z]{1,3}|[a-z]{2}|[A-Z]{2}[a-z])\.?\s*-\s*|-\s*)?([\s\S]*)$/;

/** Two small letters are capitals typed lower-case; "DFe" is spelled as it is. */
function initialsOf(tag: string | undefined): Nullable<string> {
  if (tag === undefined) return null;
  return /^[a-z]{2}$/.test(tag) ? tag.toUpperCase() : tag;
}

function dayOf(month: number, date: number, year: number): Nullable<string> {
  const day = [year < 100 ? 2000 + year : year, month, date]
    .map((part) => String(part).padStart(2, '0'))
    .join('-');
  return utcDay(day) ? day : null;
}

/** A survey's notes log is its staff field `<survey>.notes` (Salesforce's `Notes__c`). */
export function isNotesLog(code: string): boolean {
  return code.endsWith('.notes');
}

export function noteEntries(notes: string): INoteEntry[] {
  return notes
    .split(BETWEEN)
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const match = ENTRY.exec(part);
      const day = match ? dayOf(Number(match[1]), Number(match[2]), Number(match[3])) : null;
      return match && day
        ? { day, initials: initialsOf(match[4]), text: match[5].trim() }
        : { day: null, initials: null, text: part };
    });
}

const YEAR_MS = 365 * 24 * 60 * 60 * 1000;

/**
 * The entries that speak for the advisor's current exclusion: dated no earlier than a year before
 * `since` (when the reason was set). An undated part goes with the dated entry before it — a `//`
 * typed inside an entry's text — and counts where it leads the log. A note from an earlier
 * exclusion — "sit out the next ranking" written in 2019 — says nothing about this one.
 */
export function entriesSince(entries: readonly INoteEntry[], since: number): INoteEntry[] {
  const from = since - YEAR_MS;
  let day: Nullable<string> = null;
  return entries.filter((entry) => {
    day = entry.day ?? day;
    return day === null || Date.parse(day) >= from;
  });
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** How many days `day` lies outside a row's period — 0 inside it, or for an undated entry. */
function daysOutside(row: INoteInitials, day: Nullable<string>): number {
  if (day === null) return 0;
  if (row.validFrom !== null && day < row.validFrom) return (Date.parse(row.validFrom) - Date.parse(day)) / DAY_MS;
  if (row.validTo !== null && day > row.validTo) return (Date.parse(day) - Date.parse(row.validTo)) / DAY_MS;
  return 0;
}

/**
 * Who wrote an entry (SR-494): the person its initials stood for on its day — by the pair, since a
 * tag changed hands over the years (KH, CG, SD, JS). A day outside every period of the tag goes to
 * the nearest one: a year typed wrong is not another person. Null where the tag is unknown, or its
 * period is one nobody could be named for.
 */
export function whoWrote(
  entry: Pick<INoteEntry, 'day' | 'initials'>,
  table: readonly INoteInitials[]
): Nullable<INoteInitials> {
  if (entry.initials === null) return null;
  let best: Nullable<INoteInitials> = null;
  let distance = Infinity;
  for (const row of table) {
    if (row.initials !== entry.initials) continue;
    const outside = daysOutside(row, entry.day);
    if (outside < distance) {
      best = row;
      distance = outside;
    }
  }
  return best?.name ? best : null;
}
